import React, { useState } from 'react';
import { API } from 'aws-amplify';
import { v4 as uuidv4 } from 'uuid';

import { Lead, LeadStatus, LeadSource } from '../../context/model/types';
import { createLead, updateLead, deleteLead } from '../../graphql/mutations';
import { useUsers } from '../../context/users';

interface Props {
  lead: Lead;
  setShow: (v: boolean) => void;
}

const STATUS_OPTIONS: LeadStatus[] = ['new', 'contacted', 'responded', 'converted'];

const SOURCE_OPTIONS: { value: LeadSource; label: string }[] = [
  { value: 'google_ads', label: 'Google Ads' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'referral', label: 'Referral' },
  { value: 'school_forum', label: 'School forum' },
  { value: 'found_online', label: 'Found online' },
  { value: 'other', label: 'Other' },
];

const LeadModal: React.FC<Props> = ({ lead, setShow }) => {
  const { setLeads, facilityInfo } = useUsers();
  const isNew = !lead.id;

  const [name, setName] = useState(lead.name ?? '');
  const [phone, setPhone] = useState(lead.phone ?? '');
  const [email, setEmail] = useState(lead.email ?? '');
  const [status, setStatus] = useState<LeadStatus>(lead.status ?? 'new');
  const [source, setSource] = useState<LeadSource | ''>(lead.source ?? '');
  const [sourceCustom, setSourceCustom] = useState(lead.sourceCustom ?? '');
  const [lastContacted, setLastContacted] = useState(lead.lastContacted ?? '');
  const [contactMethod, setContactMethod] = useState(lead.contactMethod ?? '');
  const [followUpAt, setFollowUpAt] = useState(lead.followUpAt ?? '');
  const [anticipatedDate, setAnticipatedDate] = useState(lead.anticipatedDate ?? '');
  const [notes, setNotes] = useState(lead.notes ?? '');

  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const buildPayload = (): Partial<Lead> => ({
    name: name.trim(),
    phone: phone.trim() || null,
    email: email.trim() || null,
    status,
    source: source || null,
    sourceCustom: source === 'other' ? (sourceCustom.trim() || null) : null,
    lastContacted: lastContacted || null,
    contactMethod: contactMethod.trim() || null,
    followUpAt: followUpAt || null,
    anticipatedDate: anticipatedDate.trim() || null,
    notes: notes.trim() || null,
  });

  const handleSave = async () => {
    if (!name.trim()) {
      alert('Name is required.');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const newId = uuidv4();
        const input = {
          id: newId,
          facilityID: lead.facilityID || facilityInfo?.id || null,
          dateCreated: lead.dateCreated || new Date().toISOString().slice(0, 10),
          ...buildPayload(),
        };
        await API.graphql({ query: createLead, variables: { input } });
        setLeads(prev => [...prev, input as Lead]);
      } else {
        const input = { id: lead.id, ...buildPayload() };
        await API.graphql({ query: updateLead, variables: { input } });
        setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, ...input } as Lead : l));
      }
      setShow(false);
    } catch (e) {
      console.error('Error saving lead:', e);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleConvert = async () => {
    if (!window.confirm(`Mark "${name}" as converted? They'll need to complete signup separately.`)) return;
    setConverting(true);
    try {
      const input = { id: lead.id, ...buildPayload(), status: 'converted' as LeadStatus };
      await API.graphql({ query: updateLead, variables: { input } });
      setLeads(prev => prev.map(l => l.id === lead.id ? { ...l, ...input } as Lead : l));
      setShow(false);
    } catch (e) {
      console.error('Error marking converted:', e);
      alert('Failed to update. Please try again.');
    } finally {
      setConverting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete this lead? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await API.graphql({ query: deleteLead, variables: { input: { id: lead.id } } });
      setLeads(prev => prev.filter(l => l.id !== lead.id));
      setShow(false);
    } catch (e) {
      console.error('Error deleting lead:', e);
      alert('Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
      onClick={e => { if (e.target === e.currentTarget) setShow(false); }}
    >
      <div
        style={{
          backgroundColor: '#fff',
          borderRadius: 10,
          padding: '24px 28px',
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        }}
      >
        <h2 style={{ margin: '0 0 2px', fontSize: 17, fontWeight: 600, color: '#111827' }}>
          {isNew ? 'Add Lead' : 'Lead Details'}
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6B7280' }}>
          {isNew ? 'Manually log a lead from a call, text, or referral.' : `Created ${lead.dateCreated || '—'}`}
        </p>

        <Row label="Name">
          <Input value={name} onChange={setName} />
        </Row>
        <Row label="Phone">
          <Input value={phone} onChange={setPhone} placeholder="+15105551234" />
        </Row>
        <Row label="Email">
          <Input value={email} onChange={setEmail} placeholder="(needed to convert)" />
        </Row>

        <Row label="Status">
          <select value={status} onChange={e => setStatus(e.target.value as LeadStatus)} style={selectStyle}>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Row>

        <Row label="Source">
          <select value={source} onChange={e => setSource(e.target.value as LeadSource | '')} style={selectStyle}>
            <option value="">— none —</option>
            {SOURCE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Row>
        {source === 'other' && (
          <Row label="Source detail">
            <Input value={sourceCustom} onChange={setSourceCustom} placeholder="e.g. Amelia (referral)" />
          </Row>
        )}

        <Row label="Last contacted">
          <Input value={lastContacted} onChange={setLastContacted} type="date" />
        </Row>
        <Row label="Contact method">
          <Input value={contactMethod} onChange={setContactMethod} placeholder="Call + Text" />
        </Row>
        <Row label="Follow up">
          <Input value={followUpAt} onChange={setFollowUpAt} type="date" />
        </Row>
        <Row label="Anticipated date">
          <Input value={anticipatedDate} onChange={setAnticipatedDate} placeholder="2026-05-15 or 'End of May'" />
        </Row>

        <Row label="Notes">
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </Row>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20 }}>
          {isNew ? (
            <span />
          ) : (
            <button
              onClick={handleDelete}
              disabled={deleting || saving || converting}
              style={{
                padding: '8px 12px', borderRadius: 6,
                border: 'none', background: 'transparent',
                color: '#B91C1C', fontSize: 12, cursor: 'pointer',
              }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShow(false)}
              style={{
                padding: '8px 16px', borderRadius: 6,
                border: '1px solid #D1D5DB', background: '#fff',
                fontSize: 13, cursor: 'pointer', color: '#374151',
              }}
            >
              Cancel
            </button>
            {!isNew && status !== 'converted' && (
              <button
                onClick={handleConvert}
                disabled={converting || saving || deleting}
                style={{
                  padding: '8px 16px', borderRadius: 6, border: 'none',
                  background: converting ? '#86EFAC' : '#16A34A',
                  color: '#fff', fontSize: 13, fontWeight: 600,
                  cursor: converting ? 'not-allowed' : 'pointer',
                }}
              >
                {converting ? 'Marking…' : 'Mark Converted'}
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || converting || deleting}
              style={{
                padding: '8px 16px', borderRadius: 6, border: 'none',
                background: saving ? '#C084FC' : '#7B1FA2',
                color: '#fff', fontSize: 13, fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              {saving ? 'Saving…' : isNew ? 'Add Lead' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
    <label style={{ fontSize: 12, color: '#6B7280', minWidth: 120 }}>{label}</label>
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 6,
  border: '1px solid #D1D5DB',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = { ...inputStyle, background: '#fff' };

const Input: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}> = ({ value, onChange, placeholder, type = 'text' }) => (
  <input
    type={type}
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    style={inputStyle}
  />
);

export default LeadModal;
